import React from 'react'

import { useDispatch } from '@hozana/redux/hooks'
import type { TActionSimple } from '@hozana/routines/types'
import { useScreen } from '@hozana/screen/useScreen'
import { QA } from '@hozana/tests/constants'
import { UTM } from '@hozana/tracking/constants'

import { Button } from 'elements/button/Button'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { Icon } from 'elements/media/Icon'
import { P } from 'elements/text/P'

import { ICON } from 'config/icons'
import { PAGE } from 'routes/pages'

import { JoinButton, TJoinButtonProps } from 'modules/community/components/JoinButton'
import { isNovenaOrRetreat } from 'modules/community/functions'
import { leaveCommunity, showCommunityNotifications } from 'modules/community/routines'
import { InviteButton } from 'modules/share/components/InviteButton'
import { SHARE_OBJECTS } from 'modules/share/constants'

export type TCommunitySubscriptionButtonsProps = {
  trackingSource: string
} & Pick<TJoinButtonProps, 'community'>

export const CommunitySubscriptionButtons: TFC<TCommunitySubscriptionButtonsProps> = ({
  community,
  trackingSource,
}) => {
  const screen = useScreen()
  const novenaOrRetreat = isNovenaOrRetreat(community)
  const { retreatStatus = {}, hasJoined: isMember } = community
  const dispatch = useDispatch()

  let mainButton = (
    <JoinButton
      w="100%"
      size={isMember && (screen.sm || screen.md) ? 'small' : 'bigger'}
      community={community}
      trackingSource={trackingSource}
      // No event origin here because it is already on the community's page
    >
      trans:community:words.i-subscribe
    </JoinButton>
  )

  if (novenaOrRetreat ? retreatStatus.comingSoon && isMember : isMember) {
    mainButton = undefined
  } else if (
    isMember &&
    novenaOrRetreat &&
    (retreatStatus.ongoing || retreatStatus.scheduled || retreatStatus.reliving)
  ) {
    mainButton = (
      <Button
        icon={!screen.lg ? ICON.CALENDAR : null}
        size="small"
        w="100%"
        nowrap={false}
        valueLink={{
          to: {
            pathname: PAGE.COMMUNITY_NOTIFICATIONS,
            query: { communityId: community.id, communitySlug: community.slug },
          },
        }}
      >
        {screen.lg ? (
          <Div align="middleCenter" p="5px">
            <Icon name={ICON.CALENDAR} size="2.2em" m="0 10px 0 0" />
            <P align="left">trans:community:community.edit.retreat-dates</P>
          </Div>
        ) : (
          'trans:community:word.edit'
        )}
      </Button>
    )
  } else if (
    (isMember || (novenaOrRetreat && retreatStatus.finished)) &&
    novenaOrRetreat &&
    !(retreatStatus.scheduled || retreatStatus.reliving)
  ) {
    mainButton = (
      <JoinButton
        w="100%"
        size={screen.sm || screen.md ? 'small' : 'bigger'}
        community={community}
        trackingSource={trackingSource}
        // No event origin here because it is already on the community's page
      >
        trans:community:word.restart
      </JoinButton>
    )
  }

  const leave: () => TActionSimple<string, unknown> = () => dispatch(leaveCommunity({ community, trackingSource }))
  const showNotifs: () => TActionSimple<string, unknown> = () => dispatch(showCommunityNotifications({ community }))

  const scheduledOrReliving = novenaOrRetreat && (retreatStatus.reliving || retreatStatus.scheduled)

  return (
    <Row align="middleCenter" gutter="10px" data-testid={QA.COMMUNITY_PAGE.ACTION_BUTTONS}>
      {/* mainButton */}
      {mainButton && <Cell w={isMember ? { xs: 1, sm: 1 / 3, lg: 1 } : '100%'}>{mainButton}</Cell>}

      {/* inviteButton */}
      {isMember && !novenaOrRetreat && (
        <Cell w={{ xs: 1, sm: 1 / 3, lg: 1 }}>
          <InviteButton
            w="100%"
            objectToShare={SHARE_OBJECTS.COMMUNITY}
            community={community}
            trackingSource={trackingSource}
            utmContent={UTM.CONTENT.COMMUNITY_PAGE_INVITE_BUTTON}
          />
        </Cell>
      )}

      {/* Notifications */}
      {isMember && (
        <Cell w={{ xs: 1 / 2, sm: 1 / 3, lg: 1 }}>
          <Button
            colors="greyLight"
            onClick={showNotifs}
            icon={ICON.NOTIFICATION}
            size="small"
            data-testid={QA.COMMUNITY_COMMON.NOTIFICATION_BUTTON}
            w="100%"
          >
            trans:community:word.notifications
          </Button>
        </Cell>
      )}

      {/* Leave */}
      {isMember && (
        <Cell w={{ xs: 1 / 2, sm: 1 / 3, lg: 1 }}>
          <Button
            colors="greyLight"
            onClick={leave}
            icon={ICON.LEAVE}
            size="small"
            w="100%"
            data-testid={QA.COMMUNITY_COMMON.LEAVE_BUTTON}
          >
            {scheduledOrReliving ? 'trans:community:community.stop' : 'trans:community:community.leave.word'}
          </Button>
        </Cell>
      )}
    </Row>
  )
}
